import { Link } from '@tanstack/react-router';
import { cn, formatBillingTierLabel, getBillingTierVariant, type TBillingTier } from '../../utils';
import { Badge } from '../Badge';
import { Button, IconButton } from '../Button';
import { Text } from '../Text';

export type ProjectCardPopoverLinkProps = {
  to: string;
  params?: Record<string, string>;
  state?: Record<string, any>;
  preload?: 'intent';
  /** Optional data-testid for the View Project link. */
  'data-testid'?: string;
  /** Optional flag to open project in new tab */
  openInNewTab?: boolean;
};

export type ProjectCardPopoverProps = {
  name: string;
  billingTier?: TBillingTier;
  pmName?: string;
  projectId?: string;
  address?: string;
  linkProps?: ProjectCardPopoverLinkProps;
  /** Whether the project is bookmarked (filled icon when true, outline when false). */
  bookmarked?: boolean;
  /** Called when the bookmark icon is clicked. Omit to hide the bookmark button. */
  onBookmarkClick?: (e: React.MouseEvent) => void;
  /** Whether a close button is added to the header. */
  hasCloseButton?: boolean;
  /** When set (e.g. delegated / cross-org project), shows an External chip and Organization row. */
  organizationName?: string;
};

/**
 * ProjectCardPopover displays detailed information about a project in a popover.
 * Optional bookmark icon in the header toggles bookmarked state (consumer handles persistence).
 */
export function ProjectCardPopover({
  name,
  billingTier,
  pmName,
  projectId,
  address,
  linkProps,
  bookmarked = false,
  hasCloseButton = false,
  onBookmarkClick,
  organizationName,
}: ProjectCardPopoverProps) {
  const typeLabel = billingTier ? formatBillingTierLabel(billingTier) : undefined;
  const typeColor = billingTier ? getBillingTierVariant(billingTier) : 'grey';
  const trimmedOrgName = organizationName?.trim() ?? '';
  const showExternalOrg = trimmedOrgName.length > 0;

  return (
    <div className="flex flex-col gap-3 min-w-[240px] max-w-[400px]">
      {/* Header with title and bookmark (Figma: name left, outline bookmark right) */}
      <div className={cn('flex items-start justify-between gap-2', hasCloseButton ? 'pr-7' : 'pr-0')}>
        <Text className="text-primary text-lg font-semibold break-words flex-1 min-w-0 leading-snug">{name}</Text>
        {!onBookmarkClick && <div className="w-7 h-7" />}
      </div>

      {/* Type chip and info rows container */}
      <div className="flex flex-col gap-2">
        {(typeLabel || showExternalOrg) && (
          <div className="flex flex-wrap items-center gap-2">
            {typeLabel && (
              <Badge color={typeColor} className="w-fit">
                <Text className="text-xs font-medium">{typeLabel}</Text>
              </Badge>
            )}
            {showExternalOrg && (
              <Badge color="yellow" className="w-fit">
                <Text className="text-xs font-medium">External</Text>
              </Badge>
            )}
          </div>
        )}

        {showExternalOrg && (
          <div className="flex gap-[15px] items-center">
            <Text className="text-grey-60 text-xs w-[90px] shrink-0">Organization</Text>
            <div className="flex gap-1 items-center min-w-0 flex-1">
              <Text className="text-primary text-sm font-medium truncate">{trimmedOrgName}</Text>
            </div>
          </div>
        )}

        {/* PM section */}
        {pmName && (
          <div className="flex gap-[15px] items-center">
            <Text className="text-grey-60 text-xs w-[90px] shrink-0">PM</Text>
            <div className="flex gap-1 items-center min-w-0">
              <Text className="text-primary text-sm font-medium truncate">{pmName}</Text>
            </div>
          </div>
        )}

        {/* Project ID section */}
        {projectId != null && projectId !== '' && (
          <div className="flex gap-[15px] items-start">
            <Text className="text-grey-60 text-xs w-[90px] shrink-0">Project ID</Text>
            <div className="flex gap-1 items-start min-w-0">
              <Text className="text-primary text-sm font-medium truncate">{projectId}</Text>
            </div>
          </div>
        )}

        {/* Address section */}
        {address != null && address !== '' && (
          <div className="flex gap-[15px] items-start">
            <Text className="text-grey-60 text-xs w-[90px] shrink-0">Address</Text>
            <div className="flex gap-1 items-start">
              <Text className="text-primary text-sm font-medium break-words">{address}</Text>
            </div>
          </div>
        )}
      </div>

      {/* View Project button */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          {onBookmarkClick && (
            <IconButton
              icon={bookmarked ? 'bookmarkFilled' : 'bookmark'}
              variant="hollow"
              size="icon"
              aria-label={`Bookmark ${name || 'project'}`}
              aria-pressed={bookmarked}
              data-testid="project-card-popover-bookmark"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBookmarkClick(e);
              }}
              className="shrink-0"
            >
              <Text className="text-xs font-medium">Bookmark</Text>
            </IconButton>
          )}
          {linkProps ? (
            <Button asChild variant="primary" size="small">
              <Link
                {...linkProps}
                target={linkProps.openInNewTab ? '_blank' : undefined}
                rel={linkProps.openInNewTab ? 'noopener noreferrer' : undefined}
              >
                View Project
              </Link>
            </Button>
          ) : (
            <Button variant="primary" size="small">
              View Project
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
